import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ArtistSidePanel } from "components/SidePanel/Company/CompanySidePanel";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import React, { useState } from "react";
import { LastRefreshDotLabel } from "./LastRefreshDotLabel";
import { ExternalCalendarState } from "./models";
import { ExternalCalendarType } from "./ExternalCalendarType";
import { formatMonths } from "utilities";
import ExternalCalendarHistoryDropdown from "components/dropdowns/reactselect/ExternalCalendarHistoryDropdown";
import { ExternalEventsPanel } from "./externalEvents/ExternalEventsPanel";
import WebsiteLink from "components/Website/WebsiteLink";
import { ButtonPrimary } from "components/Atoms/Button";
import axios from "axios";
import { addInterceptors } from "../../../Scripts/common/initApi";
import { formatTimeZone, getTimeZoneFromIana } from "shared/utils/dateTimeUtils";

interface ExternalCalendarDetailSectionProps {
  externalCalendar: server.dto.ExternalCalendar
  refreshUrl: string
}

export const ExternalCalendarDetailSection = (props: ExternalCalendarDetailSectionProps) => {
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [state, setState] = useState<ExternalCalendarState>({
    title: props.externalCalendar.title,
    artist: props.externalCalendar.artist,
    monthsInPast: props.externalCalendar.monthsInPast,
  });
  const { externalCalendar } = props;

  const onValueChanged = (key: string, value: any) => {
    if (key === "title") {
      setState({ ...state, title: value });
    } else if (key === "artist") {
      setState({ ...state, artist: value });
    } else if (key === "monthsInPast") {
      setState({ ...state, monthsInPast: value });
    }
  }

  const getUpdateExternalCalendarDetailInstruction = (st: ExternalCalendarState): server.dto.UpdateExternalCalendarDetailsInstruction => ({
    externalCalendarId: props.externalCalendar.externalCalendarId,
    title: st.title,
    artistId: st.artist?.artistId,
    monthsInThePast: st.monthsInPast,
  });

  const axiosClient = axios.create();
  addInterceptors(axiosClient);

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "externalCalendar",
      title: RESX.ExternalCalendar.resxExternalCalendar,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.Name,
        render: () => <div className="flex-column gap">
          <div className="flex justify-space-between">
            <div>
              <div>{state.title}</div>
              <LastRefreshDotLabel item={externalCalendar} />
            </div>
            <div>
              <ButtonPrimary
                onClick={async () => {
                  setIsRefreshing(true);
                  try {
                    const resp = await axiosClient.get<server.dto.ApiResponse>(props.refreshUrl, { systemOneApiConfig: { toastOnError: true } });
                    if (resp.data.hasErrors) {
                      setIsRefreshing(false);
                    } else {
                      window.location.reload();
                    }
                  } catch {
                    setIsRefreshing(false);
                  }
                }}
                label={RESX.Buttons.btnRefresh}
                loading={isRefreshing}
                disabled={isRefreshing}
              />
            </div>
          </div>
        </div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.externalCalendar.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: state.title,
          submitChange: (title: string) => window.CoreApi.ExternalCalendar.UpdateDetails(
            getUpdateExternalCalendarDetailInstruction({
              ...state,
              title,
            })
          ),
        },
      }, {
        key: "events",
        label: "Events",
        render: () => <div>
          <div>{externalCalendar.eventCount}</div>
          <div className="s1_small s1_gray">
            {RESX.ExternalCalendar.AppoinmentsFurtherInTheFutureThanXMonthsAreDiscarded.replace("{0}", externalCalendar.monthsInFuture.toString())}
          </div>
          <div className="s1_small s1_gray">
            {RESX.ExternalCalendar.AppointmentsOlderThanXMonthsAreDiscarded.replace("{0}", state.monthsInPast.toString())}
          </div>
        </div>
      }, {
        key: "monthsInPast",
        label: RESX.ExternalCalendar.History,
        render: () => <div>{formatMonths(state.monthsInPast)}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <ExternalCalendarHistoryDropdown
            value={value}
            onChange={(v) => {
              onChange(v, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: state.monthsInPast,
          submitChange: (monthsInPast: number) => window.CoreApi.ExternalCalendar.UpdateDetails(
            getUpdateExternalCalendarDetailInstruction({
              ...state,
              monthsInPast,
            })
          ),
        },
      }, {
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        render: () => <ArtistSidePanel
          companyId={state.artist.artistId}
          trigger={<a>{state.artist.name}</a>}
          fallbackIfNoPermission={<>{state.artist.name}</>}
        />,
        inPageEdit: {
          hoverEdit: true,
          render: ({ onChange, cancel, value }: RenderEditOptions<server.dto.ArtistSmall>) => <div className="main-section-template-fields-padding">
            <ArtistModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
              securityScope={"limitByUserAccess"}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: state.artist,
          submitChange: (artist: server.dto.ArtistSmall) => window.CoreApi.ExternalCalendar.UpdateDetails(
            getUpdateExternalCalendarDetailInstruction({
              ...state,
              artist,
            })
          ),
        }
      }, {
        key: "vendor",
        label: RESX.ExternalCalendar.Vendor,
        isEmpty: !externalCalendar.vendor,
        hidden: externalCalendar.type !== "iCalendar",
        render: () => <div>{externalCalendar.vendor}</div>,
      }, {
        key: "type",
        label: RESX.GeneralLabels.Type,
        render: () => <ExternalCalendarType type={externalCalendar.type} />,
      }, {
        key: "iCalendarUrl",
        label: "iCalendar URL",
        hidden: externalCalendar.type !== "iCalendar",
        render: () => <WebsiteLink website={externalCalendar.url} />
      }, {
        key: "timezone",
        label: RESX.DateTime.Timezone,
        isEmpty: !externalCalendar.ianaTimeZoneId,
        hidden: externalCalendar.type !== "googleCalendar",
        render: () => <div>{formatTimeZone(getTimeZoneFromIana(externalCalendar.ianaTimeZoneId, site.culture.uiLocale))}</div>,
      }, {
        key: "createdBy",
        label: RESX.RecordInformation.CreatedBy,
        render: () => <UserWithAvatar user={externalCalendar.createdBy} />,
      }, {
        key: "account",
        label: RESX.Account.resxAccount,
        render: () => <div>{externalCalendar.client.name}</div>
      }]
    }, {
      type: "custom",
      key: "externalEvents",
      render: () => <ExternalEventsPanel
        key="externalEvents"
        externalCalendarId={props.externalCalendar.externalCalendarId}
      />
    }]}
  />
}